import { useAppForm } from '@massilia/features'
import { acceptInvitationSchema, setAccessToken, useAcceptInvitationMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'

import { AuthErrorComponent } from '../../components/login/auth-error-component'
import { resolveAuthErrorMessage } from '../../lib/auth-error-message'
import { useAuthStore } from '../../stores/auth.store'

const searchSchema = z.object({
  token: z.string(),
})

export const Route = createFileRoute('/_auth/activer-mon-compte')({
  component: AcceptInvitationComponent,
  validateSearch: searchSchema,
})

function AcceptInvitationComponent() {
  const { token } = Route.useSearch()

  const navigate = useNavigate()
  const { updateAuth } = useAuthStore()
  const [submitError, setSubmitError] = useState<null | string>(null)
  const { mutateAsync: acceptInvitation } = useAcceptInvitationMutation()

  const form = useAppForm({
    defaultValues: {
      invitationToken: token,
      password: '',
      passwordConfirmation: '',
    },
    listeners: {
      onChange: () => {
        if (submitError) {
          setSubmitError(null)
        }
      },
    },
    onSubmit: async ({ value }) => {
      try {
        const user = await acceptInvitation(value)

        if (!user) {
          setSubmitError('Une erreur est survenue. Veuillez réessayer.')
          return
        }

        setAccessToken(user.accessToken)
        updateAuth({
          isAuthenticated: true,
          user: {
            email: user.email,
            firstName: user.firstName,
            id: user.id,
            lastName: user.lastName,
          },
        })
        void navigate({ to: '/' })
      } catch (error) {
        setSubmitError(
          resolveAuthErrorMessage(error, {
            forbiddenMessage: "Le lien d'invitation est invalide ou a expiré.",
          })
        )
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate(
          { validationSource: 'form', value },
          acceptInvitationSchema
        ),
    },
  })

  return (
    <>
      <h1 className="text-gray-light-900 text-center text-xl font-semibold">
        Activer mon espace client
      </h1>
      <p className="text-gray-light-600 text-center text-sm">
        Choisissez un mot de passe pour accéder à votre espace client.
      </p>
      <form
        className="flex w-full flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
      >
        <form.AppField name="password">
          {(field) => <field.PasswordField label="Mot de passe" placeholder="Mot de passe" />}
        </form.AppField>
        <form.AppField name="passwordConfirmation">
          {(field) => (
            <field.PasswordField
              label="Confirmer le mot de passe"
              placeholder="Confirmer le mot de passe"
            />
          )}
        </form.AppField>
        {submitError && <AuthErrorComponent message={submitError} />}
        <form.Subscribe selector={(state) => state.isSubmitted && !state.isValid}>
          {(hasValidationErrors) =>
            hasValidationErrors ? (
              <AuthErrorComponent message="Veuillez corriger les erreurs du formulaire." />
            ) : null
          }
        </form.Subscribe>
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              className="bg-brand-600 hover:bg-brand-800 w-full px-8 py-2 text-white"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? 'Activation en cours...' : 'Activer mon compte'}
            </Button>
          )}
        </form.Subscribe>
      </form>
      <Link to="/connexion">
        <Button className="text-brand-700 w-full hover:underline" variant="none">
          Retour
        </Button>
      </Link>
    </>
  )
}
